import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, RefreshCw, Trash2, Copy, Plug } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));

export const IntegrationsSection = () => {
  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [docs, setDocs] = useState(null);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);

  const load = useCallback(async () => {
    const [k, l, d] = await Promise.all([api.get("/integrations/keys"), api.get("/integrations/logs", { params: { limit: 25 } }), api.get("/integrations/docs")]);
    setKeys(k.data); setLogs(l.data); setDocs(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/integrations/keys", { name }); setFresh(data); setName(""); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const rotate = async (k) => {
    if (!window.confirm(`Rotate key "${k.name}"? The old key stops working immediately.`)) return;
    try { const { data } = await api.post(`/integrations/keys/${k.id}/rotate`); setFresh({ ...k, key: data.key }); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"?`)) return;
    try { await api.delete(`/integrations/keys/${k.id}`); toast.success("Key revoked"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section className="space-y-4" data-testid="integrations-section">
      <div>
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl flex items-center gap-2"><Plug className="h-5 w-5 text-emerald-400" /> Integrations (Inventory · Parcel Scan · VMS)</h2>
        <p className="mt-1 text-sm text-slate-400">Other StarAndDaisy apps pull data from Label Manager using an API key sent as the <code className="font-mono text-emerald-300">X-API-Key</code> header. Create one key per app.</p>
      </div>

      <div className="rounded-md border border-slate-800 bg-[#111827] p-5 space-y-3" data-testid="integration-docs">
        <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Endpoints · base URL <span className="text-slate-200">{BACKEND_URL}</span></div>
        {docs?.endpoints?.map((e) => (
          <div key={e.path} className="grid gap-1 border-t border-slate-800/70 pt-3 text-sm sm:grid-cols-[1fr_auto]">
            <div>
              <code className="font-mono text-xs text-blue-300"><span className="text-emerald-400">{e.method}</span> {e.path}</code>
              <div className="mt-1 text-xs text-slate-400">{e.desc}</div>
            </div>
            <span className="self-start rounded-sm border border-slate-700 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-300">{e.for}</span>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="flex flex-wrap gap-2 rounded-md border border-slate-800 bg-[#111827] p-4" data-testid="create-key-form">
        <Input data-testid="key-name-input" placeholder="Key name, e.g. Inventory Management" required value={name} onChange={(e) => setName(e.target.value)} className="max-w-sm border-slate-700 bg-slate-950" />
        <Button type="submit" data-testid="create-key-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-2 h-4 w-4" /> Create API key</Button>
      </form>

      {fresh && (
        <div className="rounded-md border border-emerald-700/60 bg-emerald-950/30 p-4" data-testid="fresh-key-panel">
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">New key for "{fresh.name}" — copy it now, it will not be shown again</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="break-all rounded-sm bg-slate-950 px-3 py-2 font-mono text-sm text-slate-100" data-testid="fresh-key-value">{fresh.key}</code>
            <Button size="sm" variant="outline" className="border-slate-700" onClick={() => copy(fresh.key)} data-testid="fresh-key-copy"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setFresh(null)} data-testid="fresh-key-dismiss">Dismiss</Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-slate-800 bg-[#111827]">
        <Table data-testid="keys-table">
          <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Name</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Key</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Calls</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Last used</TableHead>
            <TableHead className="w-24" />
          </TableRow></TableHeader>
          <TableBody>
            {keys.length === 0 && <TableRow><TableCell colSpan={5} className="text-sm text-slate-500">No API keys yet.</TableCell></TableRow>}
            {keys.map((k) => (
              <TableRow key={k.id} className="border-slate-800/70" data-testid={`key-row-${k.id}`}>
                <TableCell className="text-sm"><span className="flex items-center gap-2"><KeyRound className="h-3.5 w-3.5 text-slate-500" />{k.name}</span></TableCell>
                <TableCell className="font-mono text-xs text-slate-300">{k.prefix}…</TableCell>
                <TableCell className="font-mono text-xs">{k.calls}</TableCell>
                <TableCell className="font-mono text-xs text-slate-400">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Rotate key" onClick={() => rotate(k)} data-testid={`key-rotate-${k.id}`}><RefreshCw className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-300" title="Revoke" onClick={() => revoke(k)} data-testid={`key-revoke-${k.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-md border border-slate-800 bg-[#111827] p-4" data-testid="integration-logs">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Recent API calls</div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="logs-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="mt-2 max-h-64 overflow-y-auto font-mono text-xs">
          {logs.length === 0 && <div className="text-slate-500">No calls yet.</div>}
          {logs.map((l, i) => (
            <div key={i} className="flex flex-wrap gap-x-3 border-t border-slate-800/60 py-1.5 text-slate-300">
              <span className="text-slate-500">{new Date(l.at).toLocaleString()}</span>
              <span className="text-emerald-300">{l.key_name}</span>
              <span className="text-blue-300">{l.path}{l.query ? `?${l.query}` : ""}</span>
              <span className="text-slate-500">{l.ip}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
